import type {
  DashboardData, Equipo, EquipoGrouping, GroupingSource, SectorLike,
} from './riego.types';

export type { GroupingSource, Equipo, EquipoGrouping };

export const EQUIPO_PALETTE: string[] = [
  '#3693D5', // azul
  '#54B03A', // verde
  '#F2994A', // naranjo
  '#9B51E0', // morado
  '#EB5757', // rojo
  '#2D9CDB', // celeste
  '#F2C94C', // amarillo
  '#27AE60', // verde oscuro
  '#BB6BD9', // lila
  '#56CCF2', // celeste claro
];

// Color neutro para los sectores que no tienen equipo asociado.
export const SIN_EQUIPO_COLOR = '#9E9E9E';
export const SIN_EQUIPO_ID = '__sin_equipo__';
export const SIN_EQUIPO_NAME = 'Sin equipo';

const SIN_EQUIPO: Equipo = Object.freeze({
  id: SIN_EQUIPO_ID, name: SIN_EQUIPO_NAME, color: SIN_EQUIPO_COLOR,
});

const normalizeZoneId = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? String(num) : null;
};

export const buildEquipoGrouping = (
  source: GroupingSource | null | undefined,
): EquipoGrouping | null => {
  if (!source || source.sectors.length === 0) return null;

  const equipoByPumpSystemId = new Map<number, Equipo>(source.pumpSystems.map((ps, index) => [
    ps.id,
    { id: ps.id, name: ps.name, color: EQUIPO_PALETTE[index % EQUIPO_PALETTE.length] },
  ]));

  const equipoBySectorId: Record<string, Equipo> = {};
  const usedEquipos = new Map<number | string, Equipo>();
  let hasSinEquipo = false;

  source.sectors.forEach((sector) => {
    if (!sector.id) return;
    const equipo = sector.pumpSystemId != null
      ? equipoByPumpSystemId.get(sector.pumpSystemId)
      : undefined;

    if (equipo) {
      equipoBySectorId[sector.id] = equipo;
      usedEquipos.set(equipo.id, equipo);
    } else {
      equipoBySectorId[sector.id] = SIN_EQUIPO;
      hasSinEquipo = true;
    }
  });

  if (usedEquipos.size === 0) return null;

  const equipos: Equipo[] = Array.from(usedEquipos.values());
  if (hasSinEquipo) {
    equipos.push(SIN_EQUIPO);
  }

  return { equipos, equipoBySectorId };
};

export const getIrrigatingSectorIds = (
  dashboard: DashboardData | null | undefined,
  sectors: SectorLike[] | undefined,
): Set<string> => {
  const ids = new Set<string>();
  if (!dashboard || !sectors?.length) return ids;
  const runningZoneIds = new Set(
    dashboard.realIrrigations
      .filter((irrigation) => irrigation.status === 'running')
      .map((irrigation) => normalizeZoneId(irrigation.zoneId)),
  );
  runningZoneIds.delete(null);
  if (runningZoneIds.size === 0) return ids;
  sectors.forEach((sector) => {
    const sectorZoneId = normalizeZoneId(sector.wiseconnId);
    if (sector.id && sectorZoneId != null && runningZoneIds.has(sectorZoneId)) {
      ids.add(sector.id);
    }
  });
  return ids;
};
